package kz.dentalcrm.app.screens.quick_replies.add

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.BottomSheetDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.Text
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import compose.icons.FeatherIcons
import compose.icons.feathericons.MessageSquare
import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.add
import dentalcrm.composeapp.generated.resources.quick_replies_add_title
import dentalcrm.composeapp.generated.resources.quick_reply_message
import kz.dentalcrm.app.core.ui.components.UIButton
import kz.dentalcrm.app.core.ui.components.UITextInput
import kz.dentalcrm.app.screens.quick_replies.add.mvi.AddQuickReplyViewEvent
import kz.dentalcrm.app.screens.quick_replies.add.mvi.AddQuickReplyViewState
import kz.dentalcrm.app.theme.DefaultTheme
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

private const val MAX_MESSAGE_LENGTH = 800

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AddQuickReplyDialog(
    onAdd: () -> Unit,
    onDismiss: () -> Unit,
) {
    val modalBottomSheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)
    val viewModel = koinViewModel<AddQuickReplyViewModel>()
    val state by viewModel.viewState.collectAsState()

    LaunchedEffect(state.screenState) {
        if (state.screenState is AddQuickReplyViewState.ScreenState.Added) {
            viewModel.handleEvents(AddQuickReplyViewEvent.Reset)
            onAdd()
            onDismiss()
        }
    }

    ModalBottomSheet(
        modifier = Modifier.widthIn(max = 450.dp).imePadding(),
        onDismissRequest = {
            viewModel.handleEvents(AddQuickReplyViewEvent.Reset)
            onDismiss()
        },
        sheetState = modalBottomSheetState,
        dragHandle = { BottomSheetDefaults.DragHandle() },
        containerColor = DefaultTheme.colors.surfaceSecondary,
        content = {
            AddQuickReplyContent(state = state, event = viewModel::handleEvents)
        }
    )
}

@Composable
private fun AddQuickReplyContent(
    state: AddQuickReplyViewState,
    event: (AddQuickReplyViewEvent) -> Unit,
) {
    Column(
        modifier = Modifier
            .background(
                color = DefaultTheme.colors.surfaceSecondary,
            )
            .statusBarsPadding()
            .fillMaxWidth(),
    ) {
        var message by remember { mutableStateOf("") }

        Text(
            modifier = Modifier.padding(horizontal = 16.dp),
            text = stringResource(Res.string.quick_replies_add_title),
            style = DefaultTheme.typography.bodyLargeMedium,
            color = DefaultTheme.colors.onSurface
        )
        Spacer(modifier = Modifier.fillMaxWidth().height(16.dp))
        HorizontalDivider(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
            thickness = 1.dp,
            color = DefaultTheme.colors.onSurfaceVariant.copy(alpha = 0.2f)
        )
        Spacer(modifier = Modifier.fillMaxWidth().height(16.dp))
        UITextInput(
            modifier = Modifier.padding(horizontal = 16.dp).fillMaxWidth(),
            leadingIcon = FeatherIcons.MessageSquare,
            label = stringResource(Res.string.quick_reply_message),
            singleLine = false,
            maxLines = 10,
            keyboardOptions = KeyboardOptions(imeAction = ImeAction.Unspecified).copy(
                keyboardType = KeyboardType.Text,
                imeAction = ImeAction.Default
            ),
            maxLength = MAX_MESSAGE_LENGTH,
        ) {
            message = it
        }
        Spacer(modifier = Modifier.height(8.dp))
        Text(
            modifier = Modifier
                .padding(horizontal = 16.dp)
                .align(Alignment.End),
            text = "${message.length}/$MAX_MESSAGE_LENGTH",
            style = DefaultTheme.typography.labelRegular,
            color = DefaultTheme.colors.onSurfaceVariant
        )
        Spacer(modifier = Modifier.fillMaxWidth().height(16.dp))
        UIButton(
            modifier = Modifier.padding(horizontal = 16.dp).fillMaxWidth(),
            enabled = message.isNotEmpty(),
            text = stringResource(Res.string.add),
            isLoading = state.screenState is AddQuickReplyViewState.ScreenState.Adding,
            onClick = { event(AddQuickReplyViewEvent.AddQuickReply(message = message)) },
        )
        Spacer(modifier = Modifier.fillMaxWidth().height(32.dp).navigationBarsPadding())
    }
}
